import INVENTORY_API_BASE_URL from "../../config/api";
import "./manageinbound.css";
import Axios from "axios";
import { useState, useEffect } from "react";
import Table from "react-bootstrap/Table";
import { format } from "date-fns";

function formatInboundDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : format(date, "dd MMM yyyy");
}

function formatInboundCount(value) {
  if (value === null || value === undefined || value === "") return "—";

  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString()
    : String(value);
}

function Manageinbound() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    let isCurrent = true;

    const loadInboundRecords = async () => {
      try {
        const response = await Axios.get(
          INVENTORY_API_BASE_URL + "/getScannedInbound"
        );

        if (!isCurrent) return;

        setData(Array.isArray(response.data) ? response.data : []);
        setLoadError("");
      } catch (error) {
        console.error("Unable to load inbound records:", error);
        if (isCurrent) setLoadError("Inbound records could not be loaded.");
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadInboundRecords();

    return () => {
      isCurrent = false;
    };
  }, []);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredData = normalizedSearchTerm
    ? data.filter((row) =>
        [row?.BatchID, row?.Title, row?.orderno].some((value) =>
          String(value ?? "").toLowerCase().includes(normalizedSearchTerm)
        )
      )
    : data;

  return (
    <main className="inbound-page">
      <section className="inbound-panel" aria-label="Inbound inventory">
        <header className="inbound-toolbar">
          <div className="inbound-heading">
            <p className="inbound-eyebrow">INVENTORY / INBOUND</p>
            <h1>Manage Inbound</h1>
            <p className="inbound-description">
              Review scanned batches, GRN numbers, and received quantities.
            </p>
          </div>

          <div
            className="inbound-record-count"
            aria-live="polite"
            aria-atomic="true"
          >
            {isLoading ? (
              <span className="inbound-record-status">Loading</span>
            ) : loadError ? (
              <span className="inbound-record-status inbound-record-status--error">
                Unavailable
              </span>
            ) : (
              <>
                <strong>{filteredData.length.toLocaleString()}</strong>
                <span>{filteredData.length === 1 ? "record" : "records"}</span>
              </>
            )}
          </div>
        </header>

        <div className="inbound-controls">
          <div className="inbound-search-wrap">
            <svg
              className="inbound-search-icon"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="10.8" cy="10.8" r="6.8" />
              <path d="m16 16 4.4 4.4" />
            </svg>
            <input
              className="inbound-search"
              type="search"
              aria-label="Search by batch ID, title, or GRN number"
              placeholder="Search inbound records"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <p className="inbound-search-hint">
            Search by batch ID, title, or GRN number
          </p>
        </div>

        <div className="inbound-content">
          {isLoading ? (
            <div className="inbound-state" role="status">
              <span className="inbound-spinner" aria-hidden="true" />
              <span>Loading inbound records…</span>
            </div>
          ) : loadError ? (
            <div className="inbound-state inbound-state--error" role="alert">
              <strong>Inbound data is unavailable</strong>
              <span>{loadError}</span>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="inbound-state inbound-state--empty" role="status">
              <strong>
                {normalizedSearchTerm
                  ? "No matching inbound records"
                  : "No inbound records yet"}
              </strong>
              <span>
                {normalizedSearchTerm
                  ? "Try a different batch ID, title, or GRN number."
                  : "Scanned inbound batches will appear here."}
              </span>
            </div>
          ) : (
            <Table
              className="inbound-table"
              aria-label="Scanned inbound batches"
            >
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Batch ID</th>
                  <th scope="col">Title</th>
                  <th scope="col">GRN No.</th>
                  <th scope="col">Count</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, index) => {
                  const rowKey =
                    row?._id ??
                    [row?.orderno ?? "inbound", row?.BatchID ?? "batch", index]
                      .join("-");
                  const title =
                    row?.Title === null ||
                    row?.Title === undefined ||
                    row.Title === ""
                      ? "—"
                      : String(row.Title);
                  const batchId =
                    row?.BatchID === null ||
                    row?.BatchID === undefined ||
                    row.BatchID === ""
                      ? "—"
                      : String(row.BatchID);
                  const grnNumber =
                    row?.orderno === null ||
                    row?.orderno === undefined ||
                    row.orderno === ""
                      ? "—"
                      : String(row.orderno);

                  return (
                    <tr key={rowKey}>
                      <td data-label="Date" className="inbound-column--date">
                        {formatInboundDate(row?.date)}
                      </td>
                      <td data-label="Batch ID">
                        <span className="inbound-batch-id">{batchId}</span>
                      </td>
                      <td
                        data-label="Title"
                        className="inbound-column--title"
                        title={title === "—" ? undefined : title}
                      >
                        {title}
                      </td>
                      <td data-label="GRN No.">
                        <span className="inbound-grn">{grnNumber}</span>
                      </td>
                      <td data-label="Count">
                        <span className="inbound-count">
                          {formatInboundCount(row?.count)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </section>
    </main>
  );
}

export default Manageinbound;
